/**
 * Tenant-configurable form fields, driven by core_config.ccp_properties and, for
 * labels, core_config.field_label.
 *
 * WHY THIS EXISTS
 * Customers ask for their own wording, their own mandatory fields and their own
 * subset of a form. Doing that by editing the shared Spanish translations or by
 * hardcoding a tenant's field list into a shared container changes the screen for
 * every tenant on the release: `common:label.accountType` alone is referenced by
 * 98 files, so renaming it for one client renames it for all of them.
 *
 * The platform already solved this once. MultiAddressForm takes its `landmarkLabel`
 * and `extralineLabel` straight from ccp properties. This generalises that idea so
 * any form built from a `dataField` array can be relabelled, hidden, made mandatory
 * or unlocked per tenant with no code change.
 *
 * PROPERTIES - all optional. Absent means "platform default", so a tenant that
 * configures nothing sees exactly what it sees today.
 *
 *   <prefix>HiddenFields     csv of field names to remove from the form
 *   <prefix>RequiredFields   csv of field names to mark mandatory
 *   <prefix>EditableFields   csv of field names to force-enable, for fields a
 *                            platform rule would otherwise disable
 *   <prefix>PlainTextFields  csv of field names to strip the lookup icon from,
 *                            turning a picker into a free text box
 *   <prefix>OptionalFields   csv of field names to make optional
 *   <prefix>ReadOnlyFields   csv of field names to show but not edit
 *   <prefix>RegionFields     csv of address fields fed by core_config.address_region,
 *                            parent first, e.g. state,city,district (applyRegionOptions)
 *   <prefix>FieldOrder       csv of field names shown first, in that order; the
 *                            fields it does not name follow in their platform order
 *   <prefix>OptInFields      csv of entries marked optIn to show (filterOptInFields);
 *                            an optIn entry is left out for tenants that do not list it
 *   <prefix>.<fieldName>Label[.<lang>], <prefix>.<fieldName>Option.<VALUE>[.<lang>]
 *                            the same as the unscoped properties below, for one form
 *                            only; contact and address forms share field names with
 *                            other screens (firstName, state), so they read only
 *                            these (scopedOnly). Only these turn a text field into
 *                            a dropdown. The customerSearch labels fall back to the
 *                            contact and address ones (getScopedLabel). A tenant keeps
 *                            these labels in core_config.field_label, which arrives in
 *                            the same form (fieldLabelProperties)
 *   <prefix>.<fieldName>Placeholder[.<lang>]
 *                            hint shown in an empty field
 *   <prefix>.<fieldName>Default
 *                            value a new record starts with (getTenantDefaults)
 *   defaultCountry           a new address's country. The only key for it: region
 *                            preloads and resets ignore address.countryDefault
 *   <fieldName>Label         literal label for one field, e.g. typeLabel
 *   <fieldName>Label.<lang>  the same, for one language, e.g. typeLabel.es
 *   <fieldName>Option.<VALUE>[.<lang>]
 *                            offer this value in that field's dropdown, with this
 *                            wording, e.g. subTypeOption.DAIRY = Lecherias
 *   <fieldName>OptionOrder   csv of option values listed first, in that order; the
 *                            rest follow in label order. Scoped like the options,
 *                            e.g. contact.identityDocumentOptionOrder
 *   <fieldName>When.<VALUE>  target:value pairs fixed while the field holds VALUE,
 *                            e.g. typeWhen.PREPAID = subType:SUBTYPE_01
 *   <fieldName>When.*        the same, for any other non-empty value
 *
 * FIELD RULES. Some tenants tie fields together: choosing one value decides
 * another. While a field holds a value with a rule, each target is set to its
 * value and locked; a dropdown target is narrowed to that single option. A target
 * need not be in the dataField (the create screen's account category is not), so
 * the caller reads getForcedValues to set and lock controls it renders itself.
 *
 * DROPDOWNS. A select normally takes its options from the platform's own list for
 * that enum. When a tenant sets one or more <fieldName>Option properties, those
 * become the whole list for that field: only the values it named are offered, and
 * each carries the tenant's wording. Setting none leaves the platform list alone.
 * Options are sorted by label, which is how the platform sorts its own dropdowns,
 * unless <fieldName>OptionOrder puts some of them first.
 *
 * A dropdown a screen renders itself, outside any dataField (the create screen's
 * account category, an order's subscription category), takes the same list through
 * tenantSelectProps, and coerceToTenantOption keeps the value it holds on that list.
 * A prepaid-only tenant names just accountCategoryOption.PREPAID, so its accounts
 * open, and are saved, as PREPAID instead of the platform's POSTPAID default.
 *
 * The stored value is still the platform enum value, so credit profile matching,
 * reporting and every other consumer keep working on a vocabulary they understand.
 *
 * LABEL RESOLUTION. A tenant's wording can differ by language, so a label is
 * looked up most specific first and falls back to the platform default:
 *
 *   <fieldName>Label.<locale>   exact locale, e.g. typeLabel.es-CR
 *   <fieldName>Label.<language> base language, e.g. typeLabel.es
 *   <fieldName>Label            locale independent, for proper nouns and codes
 *   the platform i18n key       unchanged, so unconfigured tenants are untouched
 *
 * A tenant whose wording is the same in every language sets only the plain
 * property. A tenant that needs both sets the language variants, and one
 * language's wording never leaks into the other.
 *
 * `prefix` defaults to `customer`, so the customer screens read
 * customerHiddenFields, customerRequiredFields and so on. Other hubs can pass
 * their own prefix and reuse the same mechanism.
 *
 * A literal label works because InputGroup renders `{t(label)}` and i18next
 * returns the key unchanged when it is not a known key. So a value of
 * "Tarifa ARESEP" renders as "Tarifa ARESEP", while leaving the property unset
 * keeps the existing `common:label.accountType` lookup.
 *
 * ccp_properties.value is varchar(255). A csv of field names fits comfortably.
 * A tenant keeps its labels in core_config.field_label instead, one row per label
 * and language, so ccp_properties holds settings only.
 *
 * SHARED. ui-core and selfcare carry identical copies of this file, pinned by
 * tenantFieldConfigChecksum.test.js in each repo: selfcare's profile form and agent
 * search read the SAME ccp properties as the back office customer screens. Change
 * both copies together and update the checksum in both tests.
 *
 * KEYS THE SERVER READS TOO. These change what is stored, so the screens and the
 * engine must agree on them:
 *   addressOptionalFields, contactOptionalFields
 *                            fields a record may leave blank. A blank one the input needs
 *                            or the form holds is sent and stored as '' (fillOptionalBlanks);
 *                            a blank country takes defaultCountry here and the tenant
 *                            profile's country in the engine. 'id' lets the engine number
 *                            several contacts or addresses created without one.
 *   addressRegionFields      the only fields getAddressRegions answers for
 *   fieldLabelScopes         the forms whose labels getFieldLabels returns; without it
 *                            the labels query is not sent at all
 * A tenant may also set two keys only the engine reads: contactFullNameFormat (which
 * name parts, in which order, make the name on e-invoices and receipts) and
 * taxAddressFallback (blank tax address fields taken from the tenant profile).
 */

/**
 * The name an entry is addressed by, whichever kind of control it is.
 *
 * The customer forms build entries as fieldName / fieldSelect / fieldDate. The
 * search forms, which extend SearchComponent, use a flatter { name, label }
 * shape instead. `name` is checked last so nothing about the form shapes
 * changes; it only lets the same tenant config reach the search screens, where
 * a field carries the same name as it does on the form it searches.
 */
export const fieldKeyOf = item => {
  if (!item) return null;
  return item.fieldName || item.fieldSelect || item.dateSelect || item.fieldDate || item.name || null;
};

/**
 * True when the entry belongs to a search form rather than a customer form.
 *
 * The two render through different components and read different keys, and
 * getting it wrong is silent in both directions, so the shape is detected from
 * the entry instead of being assumed:
 *
 *   customer forms   InputGroup      labelFilter   optionSelect
 *   search forms     GenericInput    label         options
 *
 * GenericInput resolves its list as `tOptions ? t(tOptions)() : options`, so a
 * search entry needs `options` set AND `tOptions` removed. Writing optionSelect
 * there would leave the dropdown empty, because GenericInput never reads it.
 */
const isSearchEntry = item =>
  !!item &&
  !Object.prototype.hasOwnProperty.call(item, 'labelFilter') &&
  Object.prototype.hasOwnProperty.call(item, 'label');

/** Write a tenant label onto an entry using whichever key that entry reads. */
export const setLabelOn = (item, label) => {
  const next = { ...item };
  if (!label) return next;
  if (isSearchEntry(item)) next.label = label;
  else next.labelFilter = label;
  return next;
};

/**
 * Replace an entry's dropdown with the tenant's own list, in the shape that
 * entry's renderer reads. Naming any option for a field replaces the platform
 * list for that field only. Mutates, so pass a copy.
 */
export const setOptionsOn = (item, options) => {
  if (!options) return item;
  const next = item;
  if (isSearchEntry(item)) next.options = options;
  else next.optionSelect = options;
  delete next.tOptions;
  return next;
};

/** Raw value of one ccp property, or '' when it is not set. */
export const getCcpValue = (ccpPropertyList, property) => {
  if (!ccpPropertyList || !ccpPropertyList.length || !property) return '';
  const found = ccpPropertyList.find(item => item && item.property === property);
  return found && found.value ? found.value : '';
};

/** A ccp property read as a comma separated list, trimmed and without blanks. */
export const getCcpList = (ccpPropertyList, property) => {
  const raw = getCcpValue(ccpPropertyList, property);
  if (!raw) return [];
  return raw
    .split(',')
    .map(part => part.trim())
    .filter(part => part.length > 0);
};

/**
 * One property, resolved most specific locale first: base.<locale>, then
 * base.<language>, then base on its own. '' when none of them is set.
 */
export const resolveLocalized = (ccpPropertyList, baseProperty, language) => {
  if (!baseProperty) return '';
  const candidates = [];

  if (language) {
    candidates.push(`${baseProperty}.${language}`);
    const base = String(language).split('-')[0];
    if (base && base !== language) candidates.push(`${baseProperty}.${base}`);
  }
  candidates.push(baseProperty);

  const found = candidates.map(property => getCcpValue(ccpPropertyList, property)).find(value => value);
  return found || '';
};

/**
 * `items` with the ones whose key is named in `order` first, in that order; the
 * rest keep their current order after them. `items` itself is not touched.
 */
const orderByList = (items, order, keyOf) => {
  if (!order.length) return items;
  const rank = item => {
    const index = order.indexOf(keyOf(item));
    return index === -1 ? order.length : index;
  };
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => rank(a.item) - rank(b.item) || a.index - b.index)
    .map(entry => entry.item);
};

/**
 * The tenant's label for one field, or '' when the platform label should stand.
 */
export const getTenantLabel = (ccpPropertyList, key, language) =>
  key ? resolveLocalized(ccpPropertyList, `${key}Label`, language) : '';

/**
 * Forms that reuse another form's wording for the fields they share. Many account search
 * filters and columns are contact or address fields, so a tenant labels those once; a
 * contact or address label applies to the search field of the same name.
 */
const LABEL_FALLBACK_PREFIXES = { customerSearch: ['contact', 'address'] };

/**
 * The tenant's label for <prefix>.<key>, else for the same field on the forms that prefix
 * falls back to (LABEL_FALLBACK_PREFIXES); '' when none is set.
 */
export const getScopedLabel = (ccpPropertyList, prefix, key, language) =>
  [prefix, ...(LABEL_FALLBACK_PREFIXES[prefix] || [])]
    .map(scope => getTenantLabel(ccpPropertyList, `${scope}.${key}`, language))
    .find(label => label) || '';

/**
 * core_config.field_label rows (getFieldLabels) as the properties this module reads:
 * <scope>.<field>Label.<language>, or <scope>.<field>Option.<optionValue>.<language> for a
 * dropdown option. An empty scope or language leaves that part out. The App saga appends
 * them to the ccp property list, so the labels stay out of ccp_properties.
 */
export const fieldLabelProperties = labels =>
  (labels || [])
    .filter(row => row && row.field && row.label)
    .map(({ scope, field, optionValue, language, label }) => ({
      property: [scope, `${field}${optionValue ? `Option.${optionValue}` : 'Label'}`, language]
        .filter(part => part)
        .join('.'),
      value: label,
    }));

/**
 * The tenant's dropdown for one field as [{ value, label }], or null when the
 * tenant has not configured one and the platform list should stand.
 *
 * Properties look like subTypeOption.DAIRY or subTypeOption.DAIRY.es. The enum
 * value never contains a dot, so whatever follows the first dot is the language.
 */
export const getTenantOptions = (ccpPropertyList, key, language) => {
  if (!key || !ccpPropertyList || !ccpPropertyList.length) return null;

  const prefix = `${key}Option.`;
  const values = [];

  ccpPropertyList.forEach(item => {
    if (!item || !item.property || item.property.indexOf(prefix) !== 0) return;
    const value = item.property.slice(prefix.length).split('.')[0];
    if (value && values.indexOf(value) === -1) values.push(value);
  });

  if (!values.length) {
    // A form's own list (<prefix>.<field>) whose labels did not arrive still offers the values
    // its OptionOrder names, worded as stored, so the dropdown never turns back into a text box.
    const order = key.indexOf('.') > -1 ? getCcpList(ccpPropertyList, `${key}OptionOrder`) : [];
    return order.length ? order.map(value => ({ value, label: value })) : null;
  }

  const options = values
    .map(value => ({ value, label: resolveLocalized(ccpPropertyList, `${prefix}${value}`, language) || value }))
    .sort((a, b) => a.label.localeCompare(b.label));
  return orderByList(options, getCcpList(ccpPropertyList, `${key}OptionOrder`), option => option.value);
};

/**
 * Props for a dropdown a screen renders itself rather than from a dataField array:
 * the tenant's list in the shape GenericInput reads, `options` with `tOptions`
 * cleared, because GenericInput prefers tOptions. With a tenant list the select
 * also cannot be cleared. A blank category is not "no choice": the engine stores
 * an account sent without one as POSTPAID.
 *
 * A tenant that names no options gets `{ tOptions }` back, the platform list
 * exactly as the screen passed it before.
 */
export const tenantSelectProps = (ccpPropertyList, key, tOptions, language) => {
  const options = getTenantOptions(ccpPropertyList, key, language);
  return options ? { options, tOptions: null, isNoClearable: true } : { tOptions };
};

/**
 * The value a field should hold given the tenant's dropdown for it: `current` when
 * the tenant names no options for that field or names this value, otherwise the
 * tenant's first option. A value the list cannot show would render blank and still
 * be submitted.
 */
export const coerceToTenantOption = (ccpPropertyList, key, current) => {
  const options = getTenantOptions(ccpPropertyList, key);
  if (!options || options.some(option => option.value === current)) return current;
  return options[0].value;
};

/**
 * What a tenant rule fixes when `field` is set to `value`, as { target: value }.
 * <field>When.<value> wins over <field>When.*; {} when neither is set.
 */
export const getFieldRuleValues = (ccpPropertyList, field, value) => {
  if (!field || value === undefined || value === null || value === '') return {};
  const raw =
    getCcpValue(ccpPropertyList, `${field}When.${value}`) || getCcpValue(ccpPropertyList, `${field}When.*`);
  return raw.split(',').reduce((forced, pair) => {
    const [target, fixed] = pair.split(':').map(part => part.trim());
    return target && fixed ? { ...forced, [target]: fixed } : forced;
  }, {});
};

/** Every value the tenant's rules fix for this record right now, as { target: value }. */
export const getForcedValues = (ccpPropertyList, data) => {
  if (!ccpPropertyList || !ccpPropertyList.length || !data) return {};
  const sources = [];
  ccpPropertyList.forEach(item => {
    const match = item && item.property && /^([A-Za-z0-9_]+)When\./.exec(item.property);
    if (match && sources.indexOf(match[1]) === -1) sources.push(match[1]);
  });
  return sources.reduce((forced, field) => ({ ...forced, ...getFieldRuleValues(ccpPropertyList, field, data[field]) }), {});
};

/**
 * Pin one form field to the value a rule fixes for it, and stop the user changing it.
 *
 * A select is narrowed to the option the rule allows, so the control still shows the
 * tenant's own wording rather than the raw enum value. Selects disable on readOnly, text
 * and date inputs on isDisable, so both are set. Fields with no rule against them come
 * back untouched.
 *
 * `data` is the record as it stands. A record written before the rule existed can hold a
 * value the rule would not allow, and dropping that value from the list leaves the
 * control rendering blank against a value it cannot show. So the current value is kept
 * alongside the allowed one whenever the two differ. The field is still locked, so this
 * cannot be used to save a value the rule forbids; it only means an older record shows
 * what it actually holds instead of showing nothing.
 *
 * Both customer screens call this, the create screen through applyTenantFieldConfig and
 * the details screen directly, so the two stay locked on the same terms.
 */
export const lockForcedField = (item, forced, data) => {
  const key = fieldKeyOf(item);
  if (!key || !forced || !Object.prototype.hasOwnProperty.call(forced, key)) return { ...item };
  const current = data ? data[key] : null;
  return {
    ...item,
    optionSelect: item.optionSelect
      ? item.optionSelect.filter(option => option.value === forced[key] || (current && option.value === current))
      : item.optionSelect,
    readOnly: true,
    isDisable: true,
  };
};

/**
 * `dataField` without its optIn entries, except those the tenant names in
 * <prefix>OptInFields. optIn marks a field added to a shared form for one tenant, so
 * a tenant that names none keeps the form it had before. Kept entries lose the flag.
 */
export const filterOptInFields = (dataField = [], ccpPropertyList = [], prefix = 'customer') => {
  const optIn = getCcpList(ccpPropertyList, `${prefix}OptInFields`);
  return dataField
    .filter(item => !item || !item.optIn || optIn.indexOf(fieldKeyOf(item)) > -1)
    .map(item => {
      if (!item || !item.optIn) return item;
      const { optIn: flag, ...rest } = item;
      return rest;
    });
};

/**
 * Apply the tenant's field configuration to a dataField array.
 *
 * Returns a new array and new entries; the caller's constants are never mutated,
 * which matters because the form constants are module level and shared.
 *
 * `scopedOnly` reads labels and options from <prefix>.<field> rows only, never the
 * unscoped <field>Label / <field>Option rows the account form and searches use.
 */
export const applyTenantFieldConfig = ({
  dataField = [],
  ccpPropertyList = [],
  prefix = 'customer',
  language = '',
  data = null,
  scopedOnly = false,
} = {}) => {
  if (!dataField || !dataField.length) return [];
  const shown = filterOptInFields(dataField, ccpPropertyList, prefix);
  if (!ccpPropertyList || !ccpPropertyList.length) return shown;

  const hidden = getCcpList(ccpPropertyList, `${prefix}HiddenFields`);
  const required = getCcpList(ccpPropertyList, `${prefix}RequiredFields`);
  const optional = getCcpList(ccpPropertyList, `${prefix}OptionalFields`);
  const editable = getCcpList(ccpPropertyList, `${prefix}EditableFields`);
  const readOnly = getCcpList(ccpPropertyList, `${prefix}ReadOnlyFields`);
  const plainText = getCcpList(ccpPropertyList, `${prefix}PlainTextFields`);
  const forced = getForcedValues(ccpPropertyList, data);

  const fields = shown
    .filter(item => {
      const key = fieldKeyOf(item);
      return !key || hidden.indexOf(key) === -1;
    })
    .map(item => {
      const key = fieldKeyOf(item);
      if (!key) return { ...item };

      const scopedOptions = getTenantOptions(ccpPropertyList, `${prefix}.${key}`, language);
      const label =
        getScopedLabel(ccpPropertyList, prefix, key, language) ||
        (scopedOnly ? '' : getTenantLabel(ccpPropertyList, key, language));
      const options = scopedOptions || (scopedOnly ? null : getTenantOptions(ccpPropertyList, key, language));
      const placeholder = resolveLocalized(ccpPropertyList, `${prefix}.${key}Placeholder`, language);

      const next = setLabelOn(item, label);
      // A text field with this form's own tenant list becomes a dropdown on that list.
      if (scopedOptions && !isSearchEntry(item) && !next.fieldSelect) next.fieldSelect = key;
      if (scopedOptions && isSearchEntry(item) && !next.type) next.type = 'select';
      setOptionsOn(next, options);
      if (optional.indexOf(key) > -1) next.required = false;
      if (required.indexOf(key) > -1) next.required = true;
      if (editable.indexOf(key) > -1) next.isDisable = false;
      if (readOnly.indexOf(key) > -1) {
        next.readOnly = true;
        next.isDisable = true;
      }
      if (placeholder) next.placeholder = placeholder;
      if (plainText.indexOf(key) > -1) delete next.fa;

      // Last, so a rule's lock beats EditableFields.
      return lockForcedField(next, forced, data);
    });
  return orderByList(fields, getCcpList(ccpPropertyList, `${prefix}FieldOrder`), fieldKeyOf);
};

/**
 * Values a new record of this form starts with, as { field: value }, from
 * <prefix>.<field>Default properties. {} when the tenant sets none.
 */
export const getTenantDefaults = (ccpPropertyList, prefix) => {
  if (!ccpPropertyList || !ccpPropertyList.length || !prefix) return {};
  const pattern = new RegExp(`^${prefix}\\.([A-Za-z0-9_]+)Default$`);
  return ccpPropertyList.reduce((defaults, item) => {
    const match = item && item.property && pattern.exec(item.property);
    return match && item.value ? { ...defaults, [match[1]]: item.value } : defaults;
  }, {});
};

/**
 * A new address record's starting values: the tenant's defaultCountry and its
 * address.<field>Default values. {} for a tenant without addressRegionFields, whose
 * new addresses start as they always did (defaultCountry predates this and has other readers).
 */
export const getAddressDefaults = ccpPropertyList => {
  if (!getRegionFields(ccpPropertyList).length) return {};
  const country = getCcpValue(ccpPropertyList, 'defaultCountry');
  return { ...getTenantDefaults(ccpPropertyList, 'address'), ...(country ? { country } : {}) };
};

/**
 * Keep a stored value that is not in the entry's dropdown as one more option, so a
 * record saved before the list existed shows what it holds instead of a blank.
 * Creatable selects already show any value, so they are left alone.
 */
export const keepStoredOption = (item, data) => {
  const key = fieldKeyOf(item);
  const value = data && key ? data[key] : null;
  const options = item && item.optionSelect;
  if (!value || !options || !options.length || item.isCreateOption) return item;
  if (options.some(option => option.value === value)) return item;
  return { ...item, optionSelect: [...options, { value, label: value }] };
};

/** A region name as people type it: case, accents and outer spaces ignored. */
const regionKey = name =>
  String(name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

/** True when two region names are the same place, one in capitals or without its accents. */
export const sameRegionName = (a, b) => !!regionKey(a) && regionKey(a) === regionKey(b);

/** The tenant's region fields for a form, parent first (<prefix>RegionFields). */
export const getRegionFields = (ccpPropertyList, prefix = 'address') => getCcpList(ccpPropertyList, `${prefix}RegionFields`);

/**
 * Turn the tenant's region fields into dropdowns fed by its address_region list.
 * `regions` is { field: [{ code, name, parentCode }] }. Each level is narrowed to the
 * entries under the value chosen one level up; when that level is empty, to the
 * entries under everything still allowed there (a district list follows the chosen
 * province while the canton is blank); with nothing chosen above, the whole level is
 * offered. Records store the name. A stored name is matched to its entry ignoring case
 * and accents, so an older record written as PARAISO still narrows the level below it,
 * and the stored text is kept as an extra option (keepStoredOption), so it keeps showing
 * as written.
 */
export const applyRegionOptions = ({ dataField = [], ccpPropertyList = [], prefix = 'address', regions = null, data = null }) => {
  const fields = getRegionFields(ccpPropertyList, prefix);
  if (!fields.length || !regions) return dataField;

  const optionsByField = {};
  let parentCodes = null;
  fields.forEach(field => {
    const level = (regions[field] || []).filter(entry => !parentCodes || parentCodes.indexOf(entry.parentCode) > -1);
    // Names repeat across parents (two cantons can each have a San Rafael); records store only the name.
    const names = level.map(entry => entry.name).filter((name, index, all) => all.indexOf(name) === index);
    optionsByField[field] = names.map(name => ({ value: name, label: name }));
    const chosen = level.find(entry => data && sameRegionName(entry.name, data[field]));
    if (chosen) parentCodes = [chosen.code];
    else if (parentCodes) parentCodes = level.map(entry => entry.code);
  });

  return dataField.map(item => {
    const key = fieldKeyOf(item);
    if (!key || !optionsByField[key] || !optionsByField[key].length) return item;
    const next = { ...item, fieldSelect: key, optionSelect: optionsByField[key], isCreateOption: false };
    return keepStoredOption(next, data);
  });
};

/** Region fields below `field`, which must be cleared when it changes. */
export const getRegionChildren = (ccpPropertyList, field, prefix = 'address') => {
  const fields = getRegionFields(ccpPropertyList, prefix);
  const index = fields.indexOf(field);
  return index > -1 ? fields.slice(index + 1) : [];
};

/**
 * What else to write when an address field changes, as { field: value }: the region
 * levels below it are cleared, and a new country clears every level. Back on the
 * tenant's defaultCountry, the cleared levels take its defaults again, as a new
 * record does. {} for a tenant without region fields, or when `current` (the record
 * being edited) already holds that value, as when the selected option is picked again.
 */
export const getRegionResets = (ccpPropertyList, field, value, current = null, prefix = 'address') => {
  if (current && current[field] === value) return {};
  const fields =
    field === 'country' ? getRegionFields(ccpPropertyList, prefix) : getRegionChildren(ccpPropertyList, field, prefix);
  const defaults =
    field === 'country' && value && value === getCcpValue(ccpPropertyList, 'defaultCountry')
      ? getTenantDefaults(ccpPropertyList, prefix)
      : {};
  return fields.reduce((resets, name) => ({ ...resets, [name]: defaults[name] || '' }), {});
};

// The address parts the account inputs require: a blank optional one must still be sent.
const REQUIRED_INPUT_FIELDS = { address: ['street', 'city', 'state', 'postalCode', 'country'] };

/**
 * A record as it is sent for a tenant with <prefix>OptionalFields. A blank optional field is
 * sent as '' (and stored as such) when the input requires it (the address parts) or the form
 * already holds it; a field the form does not hold is never added. A blank country is sent as
 * defaultCountry, or left out when the tenant has none (the Country scalar rejects ''). 'id' is
 * never added, so the engine numbers the records. A tenant without the list gets the record
 * back unchanged. Returns a new object.
 */
export const fillOptionalBlanks = (record, ccpPropertyList, prefix) => {
  if (!record) return record;
  const inputFields = REQUIRED_INPUT_FIELDS[prefix] || [];
  const next = { ...record };
  getCcpList(ccpPropertyList, `${prefix}OptionalFields`)
    .filter(
      field =>
        field !== 'id' &&
        (inputFields.indexOf(field) > -1 || Object.prototype.hasOwnProperty.call(record, field))
    )
    .forEach(field => {
      if (next[field] !== undefined && next[field] !== null && next[field] !== '') return;
      if (field !== 'country') {
        next[field] = '';
        return;
      }
      const country = getCcpValue(ccpPropertyList, 'defaultCountry');
      if (country) next.country = country;
      else delete next.country;
    });
  return next;
};

/**
 * A screen's submit-time mandatory list, adjusted for the tenant: its RequiredFields are
 * added, its HiddenFields and OptionalFields removed. The platform list stays the base, so
 * a tenant that configures nothing is checked exactly as before.
 */
export const adjustRequiredKeys = (baseKeys = [], ccpPropertyList = [], prefix) => {
  const required = getCcpList(ccpPropertyList, `${prefix}RequiredFields`);
  const dropped = [
    ...getCcpList(ccpPropertyList, `${prefix}HiddenFields`),
    ...getCcpList(ccpPropertyList, `${prefix}OptionalFields`),
  ];
  return [...baseKeys, ...required.filter(key => baseKeys.indexOf(key) === -1)].filter(
    key => dropped.indexOf(key) === -1
  );
};

/** True when the tenant sets any Hidden, Optional or Required fields list for one of these prefixes. */
export const hasRequiredKeysConfig = (ccpPropertyList = [], prefixes = []) =>
  prefixes.some(prefix =>
    ['Hidden', 'Optional', 'Required'].some(kind => getCcpList(ccpPropertyList, `${prefix}${kind}Fields`).length)
  );

export default applyTenantFieldConfig;
